/**
 * Soul Engine Pulse — compact CEO-dashboard widget showing the
 * Customer Intelligence health at a glance: members synced, Soul
 * Sheets generated, drafts pushed to Zoho, sync freshness.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Sparkles, Users, ChevronRight, RefreshCw } from 'lucide-react';

const STATUS_DOT = { green: '#1f5736', amber: '#A86B2C', red: '#A32D2D' };

export default function SoulEnginePulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/ci-health?client_id=jlr');
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const { zoho_crm: crm, soul_engine: soul } = data;
  const fmt = (n) => (n || 0).toLocaleString('en-IN');

  return (
    <div
      className="bg-white rounded-lg border p-4 cursor-pointer hover:bg-gray-50 transition"
      style={{ borderColor: '#E5E1DC' }}
      onClick={() => navigate('/client-intelligence/jlr')}
      data-testid="ceo-soul-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="rounded p-2" style={{ background: '#1C304A' }}>
          <Sparkles size={14} style={{ color: '#E5C9A0' }} />
        </div>
        <div>
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Customer Intelligence · JLR</h3>
          <p className="text-[10.5px]" style={{ color: '#6B7280' }}>
            Soul Engine + member roster · click to open
          </p>
        </div>
        <span className="ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{
          background: '#F4F1EB', color: STATUS_DOT[data.overall_status] || '#6B7280',
        }}>
          ● {data.overall_status}
        </span>
        <button onClick={(e) => { e.stopPropagation(); load(); }} data-testid="ceo-soul-pulse-refresh"
          className="p-1 rounded hover:bg-gray-100" style={{ color: '#9CA3AF' }}>
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
        <ChevronRight size={14} style={{ color: '#9CA3AF' }} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[11.5px]">
        <PulseStat
          label="Total members"
          value={fmt(crm.total_members)}
          subtext={`${fmt(crm.contacts_synced_last_run || 0)} synced last run`}
          icon={<Users size={11} />}
        />
        <PulseStat
          label="Soul forms completed"
          value={`${fmt(crm.soul_forms_completed)}`}
          subtext={`${crm.soul_form_pct ?? 0}% coverage`}
          accent={(crm.soul_form_pct ?? 0) > 50 ? '#1f5736' : '#A86B2C'}
        />
        <PulseStat
          label="Soul Sheets generated"
          value={fmt(soul.profiles_generated_total)}
          subtext={`${fmt(soul.profiles_generated_this_month)} this month`}
        />
        <PulseStat
          label="Pushed to Zoho"
          value={fmt(soul.pushed_to_zoho_this_month)}
          subtext="this month"
        />
        <PulseStat
          label="Coverage"
          value={`${soul.coverage_pct ?? 0}%`}
          subtext="profiles / members"
          accent={(soul.coverage_pct ?? 0) > 30 ? '#1f5736' : '#A86B2C'}
        />
      </div>
    </div>
  );
}

function PulseStat({ label, value, subtext, icon, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1" style={{ color: '#9CA3AF' }}>
        {icon} {label}
      </div>
      <div className="text-lg font-bold tabular-nums mt-0.5" style={{ color: accent || '#1B2A4A' }}>{value}</div>
      <div className="text-[9.5px]" style={{ color: '#9CA3AF' }}>{subtext}</div>
    </div>
  );
}
